import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../../components/Dashboard/Header/Header';
import Sidebar from '../../components/Dashboard/Sidebar/Sidebar';
import {
  ChevronRight,
  Share2,
  Edit3,
  Layout,
  ClipboardList,
  FileText,
  Receipt,
  CheckSquare,
  IndianRupee,
  Wallet,
  Calendar,
  Activity,
  MapPin,
  Mail,
  Zap,
  Info,
  AlertTriangle,
  BarChart3,
  Users
} from 'lucide-react';
import Breadcrumbs from '../../components/Common/Breadcrumbs/Breadcrumbs';
import styles from './ProjectView.module.css';

const ProjectView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);

  // Mock data for the specific project
  const project = {
    id: id || '#PRJ-402',
    name: 'Bridge Construction P-402',
    category: 'Infrastructure',
    description: 'Replacement of Highway 101 overpass - Sector 7G',
    status: 'IN PROGRESS',
    statusType: 'success',
    metrics: {
      budget: {
        total: 1250000,
        change: '+2.4% vs plan',
        allocated: 980000
      },
      spent: {
        total: 450000,
        percentage: 36
      },
      daysRemaining: {
        total: 124,
        delay: '-5 days delay',
        estCompletion: 'Oct 24, 2024'
      },
      health: {
        status: 'Stable',
        subStatus: 'On Schedule'
      }
    },
    scope: {
      summary: 'This infrastructure project encompasses the complete teardown and reconstruction of the main overpass on Highway 101, Bridge P-402. The project involves structural reinforcement, four-lane expansion, and the integration of smart sensor technology for real-time load monitoring. Phase 1 (Foundation) is currently at 85% completion, with pre-cast beams scheduled for delivery next month.',
      metrics: [
        { label: 'STEEL REQUIRED', value: '450 Tons' },
        { label: 'CONCRETE VOL', value: '2,200 m³' },
        { label: 'PERSONNEL', value: '64 On-site' },
        { label: 'PERMITS', value: 'Approved', type: 'status' }
      ]
    },
    stakeholders: {
      client: {
        name: 'Department of Transportation',
        division: 'Public Infrastructure Division',
        logo: 'DOT'
      },
      lead: {
        name: 'Robert T. Sterling',
        role: 'Chief Structural Engineer',
        avatar: 'https://ui-avatars.com/api/?name=Robert+Sterling&background=4a5568&color=fff'
      }
    },
    recentActivity: [
      {
        type: 'success',
        icon: <Activity size={16} />,
        title: 'Steel Reinforcement Completed',
        time: 'Today, 2:45 PM by J. Miller'
      },
      {
        type: 'info',
        icon: <Info size={16} />,
        title: 'Safety Inspection Passed',
        time: 'Yesterday, 9:00 AM'
      },
      {
        type: 'warning',
        icon: <AlertTriangle size={16} />,
        title: 'Supply Chain Alert: Concrete',
        time: '2 days ago - 48h delay expected'
      }
    ]
  };

  const navItems = [
    { icon: <Layout size={18} />, label: 'Overview', active: true },
    { icon: <ClipboardList size={18} />, label: 'Work Orders' },
    { icon: <FileText size={18} />, label: 'LOIs' },
    { icon: <Receipt size={18} />, label: 'Sales Invoices' },
    { icon: <CheckSquare size={18} />, label: 'Tasks', count: 12 },
    { icon: <IndianRupee size={18} />, label: 'Expenses' },
  ];

  return (
    <div className={`${styles['project-view-container']} ${isSidebarOpen ? styles['sidebar-open'] : ''}`}>
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={() => setIsSidebarOpen(!isSidebarOpen)} />

        <main className={styles['main-content']}>
          {/* Breadcrumbs */}
          <Breadcrumbs />

          {/* Project Header */}
          <div className={styles['project-view-header']}>
            <div className={styles['header-top-row']}>
              <div className={styles['title-section']}>
                <div className={styles['title-wrapper']}>
                  <h1>{project.name}</h1>
                  <span className={`${styles['status-tag']} ${project.statusType}`}>
                    <div className={styles['dot']}></div>
                    {project.status}
                  </span>
                </div>
                <p className={styles['subtitle']}>{project.description}</p>
              </div>
              <div className={styles['header-actions']}>
                <button className={styles['btn-secondary']}>
                  <Share2 size={18} />
                  <span>Share</span>
                </button>
                <button className={styles['btn-primary']}>
                  <Edit3 size={18} />
                  <span>Edit Project</span>
                </button>
              </div>
            </div>

            {/* Sub-navigation Tabs */}
            <div className={styles['project-nav-tabs']}>
              {navItems.map((item, index) => (
                <div key={index} className={`${styles['nav-tab']} ${item.active ? styles['active'] : ''}`}>
                  {item.icon}
                  <span>{item.label}</span>
                  {item.count && <span className={styles['tab-count']}>{item.count}</span>}
                </div>
              ))}
            </div>
          </div>

          <div className={styles['dashboard-grid']}>
            {/* Top Metrics Cards */}
            <div className={styles['metrics-row']}>
              <div className={[styles['metric-card'], styles['card']].join(' ')}>
                <div className={styles['metric-header']}>
                  <div className={[styles['icon-box'], styles['budget']].join(' ')}>
                    <Wallet size={20} />
                  </div>
                  <span className={[styles['trend'], styles['positive']].join(' ')}>{project.metrics.budget.change}</span>
                </div>
                <div className={styles['metric-info']}>
                  <span className={styles['label']}>Total Budget</span>
                  <h2 className={styles['value']}>₹{project.metrics.budget.total.toLocaleString()}.00</h2>
                </div>
                <div className={styles['metric-footer']}>
                  <span className={styles['sub-label']}>Allocated</span>
                  <span className={styles['sub-value']}>₹{project.metrics.budget.allocated.toLocaleString()}</span>
                </div>
              </div>

              <div className={[styles['metric-card'], styles['card']].join(' ')}>
                <div className={styles['metric-header']}>
                  <div className={[styles['icon-box'], styles['spent']].join(' ')}>
                    <Activity size={20} />
                  </div>
                  <span className={styles['percentage-text']}>{project.metrics.spent.percentage}% of total</span>
                </div>
                <div className={styles['metric-info']}>
                  <span className={styles['label']}>Spent to Date</span>
                  <h2 className={styles['value']}>₹{project.metrics.spent.total.toLocaleString()}.00</h2>
                </div>
                <div className={styles['metric-footer-bar']}>
                  <div className={styles['progress-bar-container']}>
                    <div className={styles['progress-bar']} style={{ width: `${project.metrics.spent.percentage}%` }}></div>
                  </div>
                </div>
              </div>

              <div className={[styles['metric-card'], styles['card']].join(' ')}>
                <div className={styles['metric-header']}>
                  <div className={[styles['icon-box'], styles['remaining']].join(' ')}>
                    <Calendar size={20} />
                  </div>
                  <span className={[styles['trend'], styles['negative']].join(' ')}>{project.metrics.daysRemaining.delay}</span>
                </div>
                <div className={styles['metric-info']}>
                  <span className={styles['label']}>Days Remaining</span>
                  <h2 className={styles['value']}>{project.metrics.daysRemaining.total} Days</h2>
                </div>
                <div className={styles['metric-footer']}>
                  <span className={styles['sub-label']}>Est. Completion:</span>
                  <span className={styles['sub-value-plain']}>{project.metrics.daysRemaining.estCompletion}</span>
                </div>
              </div>

              <div className={[styles['metric-card'], styles['card']].join(' ')}>
                <div className={styles['metric-header']}>
                  <div className={[styles['icon-box'], styles['health']].join(' ')}>
                    <Zap size={20} />
                  </div>
                  <span className={styles['status-label']}>On Schedule</span>
                </div>
                <div className={styles['metric-info']}>
                  <span className={styles['label']}>Project Health</span>
                  <h2 className={styles['value-plain']}>{project.metrics.health.status}</h2>
                </div>
                <div className={styles['metric-footer-segments']}>
                  <div className={styles['health-segments']}>
                    <div className={[styles['segment'], styles['active']].join(' ')}></div>
                    <div className={[styles['segment'], styles['active']].join(' ')}></div>
                    <div className={[styles['segment'], styles['active']].join(' ')}></div>
                    <div className={styles['segment']}></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Content Layout */}
            <div className={styles['view-content-columns']}>
              <div className={styles['left-column']}>
                {/* Scope Summary */}
                <div className={[styles['content-card'], styles['card']].join(' ')}>
                  <div className={styles['card-header']}>
                    <div className={styles['header-title']}>
                      <FileText size={20} />
                      <h3>Scope Summary</h3>
                    </div>
                    <a href="#" className={styles['link']}>Full Document</a>
                  </div>
                  <p className={styles['summary-text']}>{project.scope.summary}</p>
                  {/* <div className={styles['scope-metrics-grid']}>
                    {project.scope.metrics.map((m, i) => (
                      <div key={i} className={styles['scope-metric-box']}>
                        <span className={styles['label']}>{m.label}</span>
                        <span className={`${styles['value']} ${m.type || ''}`}>{m.value}</span>
                      </div>
                    ))}
                  </div> */}
                </div>

                {/* Budget Utilization */}
                {/* <div className={[styles['content-card'], styles['card']].join(' ')}>
                  <div className={styles['card-header']}>
                    <div className={styles['header-title']}>
                      <BarChart3 size={20} />
                      <h3>Budget Utilization</h3>
                    </div>
                    <div className={styles['chart-legend']}>
                      <div className={styles['legend-item']}><span className={[styles['dot'], styles['spent']].join(' ')}></span> Spent</div>
                      <div className={styles['legend-item']}><span className={[styles['dot'], styles['budget']].join(' ')}></span> Budget</div>
                    </div>
                  </div>
                  <div className={styles['utilization-chart']}>
                    <div className={styles['chart-bars']}>
                      {[
                        { month: 'JAN', spent: 30, budget: 70 },
                        { month: 'FEB', spent: 45, budget: 85 },
                        { month: 'MAR', spent: 65, budget: 90 },
                        { month: 'APR', spent: 75, budget: 95 },
                        { month: 'MAY', spent: 55, budget: 80 },
                        { month: 'JUN', spent: 60, budget: 85 },
                      ].map((item, idx) => (
                        <div key={idx} className={styles['bar-group']}>
                          <div className={styles['bar-container']}>
                            <div className={styles['budget-bar']} style={{ height: `${item.budget}%` }}></div>
                            <div className={styles['spent-bar']} style={{ height: `${item.spent}%` }}></div>
                          </div>
                          <span className={styles['month-label']}>{item.month}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div> */}
              </div>

              <div className={styles['right-column']}>
                {/* Location */}
                <div className={[styles['content-card'], styles['card']].join(' ')}>
                  <div className={styles['card-header']}>
                    <div className={styles['header-title']}>
                      <MapPin size={20} />
                      <h3>Location</h3>
                    </div>
                  </div>
                  <div className={styles['location-map']}>
                    <img src="/assets/project_site.png" alt="Site Location" />
                    <div className={styles['map-label']}>
                      <span>Chicago, IL - Sector 7G</span>
                    </div>
                  </div>
                  <div className={styles['location-footer']}>
                    <MapPin size={16} />
                    <span>Site Access: Restricted (Badge A required)</span>
                  </div>
                </div>

                {/* Stakeholders */}
                {/* <div className={[styles['content-card'], styles['card']].join(' ')}>
                  <div className={styles['card-header']}>
                    <div className={styles['header-title']}>
                      <Users size={20} />
                      <h3>Stakeholders</h3>
                    </div>
                  </div>
                  <div className={styles['stakeholders-list']}>
                    <div className={styles['stakeholder-group']}>
                      <span className={styles['group-label']}>CLIENT INFORMATION</span>
                      <div className={styles['stakeholder-item']}>
                        <div className={styles['client-logo']}>DOT</div>
                        <div className={styles['stakeholder-info']}>
                          <h4>{project.stakeholders.client.name}</h4>
                          <p>{project.stakeholders.client.division}</p>
                        </div>
                      </div>
                    </div>
                    <div className={styles['stakeholder-group']}>
                      <span className={styles['group-label']}>PROJECT LEAD</span>
                      <div className={styles['stakeholder-item']}>
                        <img src={project.stakeholders.lead.avatar} alt="Lead" className={styles['avatar']} />
                        <div className={styles['stakeholder-info']}>
                          <h4>{project.stakeholders.lead.name}</h4>
                          <p>{project.stakeholders.lead.role}</p>
                        </div>
                        <button className={styles['icon-btn']}>
                          <Mail size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div> */}

                {/* Recent Activity */}
                <div className={[styles['content-card'], styles['card']].join(' ')}>
                  <div className={styles['card-header']}>
                    <div className={styles['header-title']}>
                      <Activity size={20} />
                      <h3>Recent Activity</h3>
                    </div>
                  </div>
                  <div className={styles['activity-timeline']}>
                    {project.recentActivity.map((activity, idx) => (
                      <div key={idx} className={styles['timeline-item']}>
                        <div className={`${styles['timeline-icon']} ${activity.type}`}>
                          {activity.icon}
                        </div>
                        <div className={styles['timeline-content']}>
                          <h4>{activity.title}</h4>
                          <p>{activity.time}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ProjectView;
